import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated, useSpring, useChain } from 'react-spring';

import PodiumCard from './PodiumCard';
import { NARROW_BREAKPOINT, MINIMIZED_BREAKPOINT } from '../boardConstants';

// How far the ribbon badge (PodiumCard.js) pokes above the card's top edge at
// scaleFactor === 1 — tune alongside the ribbon's own size/scaling approach.
const RIBBON_TOP_RESERVE = 30;

const PodiumWrapper = styled('div')`
  display: flex;
  flex-direction: ${(props) => (props.isSmall ? 'column' : 'row')};
  justify-content: ${(props) => (props.isSmall ? 'flex-start' : 'space-between')};
  gap: ${(props) => (props.isSmall ? `${Math.round(4 * props.scaleFactor)}px` : '0px')};
  padding: ${(props) => (props.activeBoards >= 2 ? '0px' : '0px 67px')};
  align-items: flex-end;
  flex-shrink: 0; /* Fix for Safari flex box wrong height issue */
  /* Reserves room above the card for the ribbon badge poking past its top edge */
  margin-top: ${(props) => `${Math.round(RIBBON_TOP_RESERVE * (props.scaleFactor - 1))}px`};

  :first-child {
    margin-left: 0px!important;
  }
  :last-child {
    margin-right: 0px!important;
  }

  .podium-card {
    width: 100%!important;
  }

  .podium-wrapper {
    position: relative;
  }
  /* Rank-order (1, 2, 3) top-to-bottom in the stacked small-row layout; the
     podium-style 2nd/1st/3rd visual reorder only makes sense for the
     side-by-side full-card layout. */
  .firstAnimatedPodium {
    order: ${(props) => (props.isSmall ? 'initial' : 2)};
  }

  .secondAnimatedPodium {
    order: ${(props) => (props.isSmall ? 'initial' : 1)};
  }

  .thirdAnimatedPodium {
    order: ${(props) => (props.isSmall ? 'initial' : 3)};
  }

  @media screen and (max-width: 991px) {
    padding: 0px!important;

    .result {
      font-size: 24px;
    }
  }
`;

// flex: 1 1 0 (not width: 100%) forces genuinely equal card widths — three
// width: 100% items only converge to equal thirds if all three can shrink
// equally, which a card with less-shrinkable content could break. minWidth: 0
// is required alongside it, or the item still won't shrink past its own
// content's min-content size.
const fullPodiumStyles = {
  flex: '1 1 0',
  minWidth: 0,
  margin: '0px 10px',
};

const smallPodiumStyles = {
  width: '100%',
};

const MultiplePodiums = ({
  activeBoards,
  boardWidth,
  leaderboardData,
  scaleFactor,
  showChange,
}) => {
  const [data, setData] = useState([]);

  const firstPodiumRef = useRef();
  const firstAnimatedPodium = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    ref: firstPodiumRef,
  });

  const accentRef = useRef();

  const secondPodiumRef = useRef();
  const secondAnimatedPodium = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    ref: secondPodiumRef,
  });

  const thirdPodiumRef = useRef();
  const thirdAnimatedPodium = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    ref: thirdPodiumRef,
  });

  const animationClasses = ['firstAnimatedPodium', 'secondAnimatedPodium', 'thirdAnimatedPodium'];
  const animationStyles = [firstAnimatedPodium, secondAnimatedPodium, thirdAnimatedPodium];
  const isSmall = boardWidth <= NARROW_BREAKPOINT;

  useChain([firstPodiumRef, accentRef, secondPodiumRef, thirdPodiumRef], [0, 0.5, 0.6, 0.9]);

  useEffect(() => {
    setData(leaderboardData.slice(0, 3));
  }, [leaderboardData]);

  return (
    <PodiumWrapper
      activeBoards={activeBoards}
      isSmall={isSmall}
      scaleFactor={scaleFactor}
    >
      {data.map((athlete, index) => (
        <animated.div
          key={athlete.userId}
          className={`podium-wrapper ${animationClasses[index] || ''}`}
          style={{
            ...(isSmall ? smallPodiumStyles : fullPodiumStyles),
            ...animationStyles[index],
          }}
        >
          <PodiumCard
            className='podium-card'
            athlete={athlete.userName}
            avatar={athlete.userPic}
            changeDirection={athlete.changeDirection}
            changeDisplayValue={athlete.changeDisplayValue}
            date={athlete.dateSet}
            place={athlete.rank}
            result={athlete.displayValue}
            showChange={showChange}
            small={isSmall}
            minimized={boardWidth <= MINIMIZED_BREAKPOINT}
            scaleFactor={scaleFactor}
          />
        </animated.div>
      ))}
    </PodiumWrapper>
  );
};

MultiplePodiums.propTypes = {
  activeBoards: PropTypes.number.isRequired,
  boardWidth: PropTypes.number.isRequired,
  leaderboardData: PropTypes.instanceOf(Array).isRequired,
  scaleFactor: PropTypes.number.isRequired,
  showChange: PropTypes.bool.isRequired,
};

export default MultiplePodiums;
